import { ColumnConfig } from './BlindTestListTypes';

/**
 * Function to sort data based on column and direction
 */
export const sortData = (data: any[], columnId: string, sortDirection: 'asc' | 'desc') => {
  if (!columnId) return data;
  
  return [...data].sort((a, b) => {
    const aValue = a[columnId];
    const bValue = b[columnId];
    
    // Handle nullish values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
    if (bValue == null) return sortDirection === 'asc' ? 1 : -1;
    
    // Sort numbers correctly
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Default string comparison
    const aString = String(aValue).toLowerCase();
    const bString = String(bValue).toLowerCase();
    
    return sortDirection === 'asc' 
      ? aString.localeCompare(bString)
      : bString.localeCompare(aString);
  });
};

/**
 * Function to filter data locally (for blindTests)
 */
export const getFilteredData = (
  currentData: any[], 
  filterValue: string, 
  filterColumn: string
) => {
  if (!filterValue || !filterColumn || !currentData) return currentData;
  
  return currentData.filter(row => {
    const cellValue = row[filterColumn];
    if (cellValue == null) return false;
    
    return cellValue.toString().toLowerCase().includes(filterValue.toLowerCase());
  });
};

/**
 * Get sorted and filtered data with pagination
 */
export const getSortedAndFilteredData = (
  currentData: any[],
  filterValue: string,
  filterColumn: string,
  orderBy: string,
  order: 'asc' | 'desc',
  currentPage: number,
  rowsPerPage: number
) => {
  // First apply filtering
  const filteredData = getFilteredData(currentData, filterValue, filterColumn);
  // Then apply sorting
  const sortedData = sortData(filteredData, orderBy, order);
  
  // For "All" rows view or large page sizes (filtering/sorting across all data),
  // handle pagination in the UI
  if (rowsPerPage === -1) {
    return sortedData;
  }
  
  // Apply pagination manually when we have all data loaded
  if (filterValue || rowsPerPage > 1000) {
    const startIndex = currentPage * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
    return sortedData.slice(startIndex, endIndex);
  }
  
  // Server pagination is already applied
  return sortedData;
};

/**
 * Create fetch all pages data function
 */
export const createFetchAllPagesData = (
  getDesignSessionData: (query?: string, pageNumber?: number, overrideRowsPerPage?: number) => Promise<void>,
  rowsPerPage: number,
  currentFilter: string
) => {
  return (query: string = currentFilter) => {
    // Only fetch all data if we're not already working with a filtered dataset 
    // or if the rowsPerPage is not already set to a large value
    if (rowsPerPage < 1000 && rowsPerPage !== -1) {
      const LARGE_PAGE_SIZE = 10000; // Large value to get all rows
      getDesignSessionData(query, 0, LARGE_PAGE_SIZE);
    }
  };
};

/**
 * Check if column should be displayed
 */
export const isColumnVisible = (columnId: string, hiddenColumns: string[]) => {
  return !hiddenColumns.includes(columnId);
};

/**
 * Get visible columns
 */
export const getVisibleColumns = (columns: ColumnConfig[], hiddenColumns: string[]) => {
  return columns.filter(column => isColumnVisible(column.id, hiddenColumns));
}; 